<template>
  <div class="review-products">
    <el-table 
      ref="tableRef"
      :data="processedProducts" 
      style="width: 100%"
      class="review-products-table"
      row-key="id"
      @selection-change="handleSelectionChange"
      @sort-change="handleSortChange"
    >
      <!-- Selection column -->
      <el-table-column type="selection" width="55" reserve-selection />
      
      <!-- Product info column -->
      <el-table-column label="Item">
        <template #default="scope">
          <div class="item-cell">
            <div class="product-image">
              <el-image 
                :src="scope.row.images?.img_200x200 || scope.row.thumbnail" 
                fit="cover"
                :alt="scope.row.name"
              />
            </div>
            <div class="product-info">
              <h4 class="product-name">{{ scope.row.name }}</h4>
              <p class="brand-name">{{ scope.row.brand?.name || scope.row.merchant_name }}</p>
              <div class="price-info">
                <span class="current-price">{{ scope.row.lowest_price }}</span>
                <span v-if="scope.row.on_sale && scope.row.origin_price" class="original-price">
                  ${{ scope.row.origin_price }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>

      <!-- URL column -->
      <el-table-column v-if="hasAnyWebsiteUrl" label="URL" max-width="150" align="left" prop="website_url">
        <template #default="scope">
          <div class="item-cell url" v-if="scope.row.website_url">
            <a 
              :href="scope.row.website_url" 
              class="product-name" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              {{ scope.row.website_url }}
            </a>
          </div>
        </template>
      </el-table-column>

      <!-- Variants column -->
      <el-table-column label="Variants" width="100" align="center" sortable prop="total_product_count">
        <template #default="scope">
          <div class="variants-cell">
            <span class="variant-count">{{ getVariantCount(scope.row) }}</span>
          </div>
        </template>
      </el-table-column>

      <!-- Receipt column -->
      <el-table-column v-if="add_type === 'storefront'" label="Receipt" width="160" align="center" prop="receipt_count">
        <template #default="scope">
          <div class="receipt-cell">
            
            <span  v-for="(item, index) in scope.row.post_to.data" :key="index"> 
              <svg-icon name="users-2" v-if="item.merchant_account_id || item.name === 'All'" /> {{ item.name }}
            </span>
          </div>
        </template>
      </el-table-column>

      <!-- Collections column -->
      <el-table-column label="Collections" width="120" align="center" sortable prop="collection_count">
        <template #default="scope">
          <div class="collections-cell">
            {{ add_type !== 'storefront' ? '1' : scope.row.collection_count  }}
          </div>
        </template>
      </el-table-column>

      <!-- Publish status column -->
      <el-table-column v-if="add_type === 'storefront'" label="Publish status" width="124" align="center" sortable prop="created_at">
        <template #default="scope">
          <div class="status-cell">
            <span
              :class="'status-tag ' + getStatusType(scope.row)"
            >
              {{ getStatusText(scope.row) }}
            </span>
            <div class="status-date" v-if="getStatusType(scope.row) !== 'status-draft'">
              {{ formatStatusDate(scope.row) }}
            </div>
          </div>
        </template>
      </el-table-column>

      <!-- Actions column -->
      <el-table-column width="60" align="right">
        <template #default="scope">
          <div class="actions-cell">
            <el-button 
              type="text" 
              size="small" 
              @click="editProduct(scope.row)"
            >
              <svg-icon name="pen" />
            </el-button>
            <el-button 
              type="text" 
              size="small" 
              @click="removeProduct(scope.row)"
              class="delete-btn"
            >
              <svg-icon name="remove" />
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup>
  import { onMounted, ref, computed, nextTick, watch, getCurrentInstance } from 'vue';
  import { ElTable, ElTableColumn, ElImage, ElButton } from 'element-plus';
  import moment from 'moment';

  const app = getCurrentInstance();
  const event = app.appContext.config.globalProperties.EventBus;

  // Table ref
  const tableRef = ref(null);

  // Props
  const props = defineProps({
    products: {
      type: Array,
      default: () => []
    },
    add_type: {
      type: String,
      default: 'storefront' // 'post' or 'collection' or 'storefront'
    },
  });

  // Computed properties for sorting
  const processedProducts = computed(() => {
    // console.log('Processing products:', props.products);

    return props.products.map(product => ({
      ...product,
      receipt_count: product.post_to.data.length || 0,
      collection_count: product.curation_collections.length || 0,
      is_checked: product.is_checked || false
    }));
  });

  // Check if any product has website_url
  const hasAnyWebsiteUrl = computed(() => {
    return props.products.some(product => product.website_url);
  });

  // Emits
  const emit = defineEmits([
    'selection-change',
    'edit-variants',
    'edit-receipt', 
    'edit-product',
    'remove-product',
    'sort-change'
  ]);

  // Methods
  const handleSelectionChange = (selection) => {
    // Update is_checked property only for original products data
    props.products.forEach(product => {
      product.is_checked = selection.some(selected => selected.id === product.id);
    });
    
    emit('selection-change', selection);
  };

  const handleSortChange = ({ column, prop, order }) => {
    emit('sort-change', { column, prop, order });
  };

  const getVariantCount = (product) => {
    // Calculate variant count based on product data
    if (product.total_color_size_map && product.total_color_size_map.length > 1) {
      return product.total_color_size_map.filter(item => item.is_selected).length;
    }
    return '---';
  };

  const editProduct = (product) => {
    event.emit('select-variant', {
      is_edit: true,
      product: product
    });
  };

  const removeProduct = (product) => {
    emit('remove-product', product);
  };

  const getStatusText = (product) => {
    if (product.scheduled_start_enabled || product.scheduled_end_enabled) {
      return 'Scheduled';
    }

    switch (product.status_string) {
      // case 'scheduled':
      //   return 'Scheduled';
      case 'disable':
        return 'Disable';
      case 'active':
        return 'Active';
      default:
        return 'Draft';
    }

    // If status is active but has scheduling information (scheduled_start or scheduled_end)
    // if (product.status === 'scheduled' && (product.scheduled_start.val || product.scheduled_end.val)) {
    //   return 'Scheduled';
    // }

    // // If product is in storefront and active (without scheduling)
    // if (product.status === 'active') {
    //   return 'Active';
    // }

    // // Default to draft status
    // return 'Draft';
  };

  const getStatusType = (product) => {
    const statusText = getStatusText(product);
    switch (statusText.toLowerCase()) {
      case 'scheduled':
        return 'status-scheduled';
      case 'active':
        return 'status-active';
      case 'disable':
        return 'status-disable';
      case 'draft':
        return 'status-draft';
    }
  };

  const formatStatusDate = (product) => {
    // Display corresponding dates based on product status
    const statusText = getStatusText(product);
    
    if (statusText === 'Scheduled') {
      // For scheduled products, show the scheduled date range
      const startDate = product.scheduled_start_enabled && product.scheduled_start?.val ? 
        moment(product.scheduled_start.val).format('MMM DD, YYYY') : 'No start date';
      const endDate = product.scheduled_end_enabled && product.scheduled_end?.val ? 
        moment(product.scheduled_end.val).format('MMM DD, YYYY') : 'No end date';
      return `${startDate} - ${endDate}`;
    } else if (statusText === 'Active') {
      // For active products (without scheduling), show when it became active
      const startDate = product.scheduled_start?.val ? moment(product.scheduled_start.val).format('MMM DD, YYYY') + ' - ' : '';
      return `${startDate}Present`;
    } else {
      // For draft products, show creation date
      return moment(product.created_at).format('MMM DD, YYYY');
    }
  };

  // Initialize table selection based on is_checked property
  const initializeSelection = () => {
    nextTick(() => {
      if (tableRef.value) {
        processedProducts.value.forEach(product => {
          if (product.is_checked) {
            tableRef.value.toggleRowSelection(product, true);
          }
        });
      }
    });
  };

  // Initialize selection on component mount
  onMounted(() => {
    initializeSelection();
  });

  // Watch for changes in products data to restore selection
  watch(() => props.products.length, () => {
    initializeSelection();
  });

  // Expose methods for parent component
  defineExpose({
    clearSelection: () => {
      if (tableRef.value) {
        tableRef.value.clearSelection();
      }
    },

    toggleAllSelection: () => {
      if (tableRef.value) {
        tableRef.value.toggleAllSelection();
      }
    },

    setSelection: (products) => {
      if (tableRef.value && products) {
        nextTick(() => {
          tableRef.value.clearSelection();
          products.forEach(product => {
            const foundProduct = processedProducts.value.find(p => p.id === product.id);
            if (foundProduct) {
              tableRef.value.toggleRowSelection(foundProduct, true);
            }
          });
        });
      }
    },

    getSelection: () => {
      return tableRef.value ? tableRef.value.getSelectionRows() : [];
    }
  });
</script>
<style lang="scss">
.review-products {
  .el-table .cell {
    padding: 0;
  }

  .review-products-table {
    .el-table__header-wrapper {
      box-sizing: border-box;

      .el-table__header {

        th {
          background-color: var(--Black-05);
          color: var(--Text-secondary);
          font: var(--medium-12);
          height: 40px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--Black-20);
          
          .cell {
            display: flex;
            align-items: center;
            color: var(--Text-primary);
            font: var(--medium-12);

            .caret-wrapper {
              position: relative;
              top: 1px;
            }
          }
        }
      }
    }

    .el-table__body-wrapper {
      .el-table__body {
        tr {
          &:hover {
            background-color: var(--Bg-01);
          }

          td {
            border-bottom: 1px solid var(--Black-20);
            padding: 12px;
            height: 80px;
            vertical-align: middle;
          }
        }
      }
    }

    // Product info column styles
    .item-cell {
      display: flex;
      align-items: center;
      gap: 12px;

      .product-image {
        width: 72px;
        height: 72px;
        border-radius: var(--radius);
        overflow: hidden;
        flex-shrink: 0;
        background-color: var(--Bg-01);

        .el-image {
          width: 100%;
          height: 100%;
          
          .el-image__inner {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }

      .product-info {
        flex: 1;
        min-width: 0;

        .product-name {
          font: var(--medium-14);
          color: var(--Text-primary);
          margin: 0 0 4px 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .brand-name {
          font: var(--medium-12);
          color: var(--Text-secondary);
          margin: 0 0 8px 0;
        }

        .price-info {
          display: flex;
          align-items: center;
          gap: 8px;

          .current-price {
            font: var(--medium-14);
            color: var(--Text-primary);
          }

          .original-price {
            font: var(--regular-12);
            color: var(--Text-secondary);
            text-decoration: line-through;
          }
        }
      }

      &.url {
        // white-space: break-spaces;
        overflow: hidden;
      }
    }

    // Variants column styles
    .variants-cell {

      .variant-count {
        font: var(--medium-14);
        color: var(--Text-primary);
      }
    }

    // Receipt column styles
    .receipt-cell {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;

      svg {
        width: 14px;
        height: 14px;
        min-height: 14px;
        min-width: 14px;
      }

      span {
        font: var(--medium-12);
        color: var(--Text-primary);
        background-color: var(--Black-05);
        padding: 2px 6px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    // Publish status column styles
    .status-cell {
      .status-tag {
        font: var(--medium-12);
        color: var(--Text-primary);
        padding: 2px 8px;
        border-radius: 4px;
        width: auto;
        display: inline-flex;

        // Scheduled status (info type)
        &.status-scheduled {
          background-color: var(--Blue-10);
          border-color: var(--Blue-20);
        }

        // Active status (success type)
        &.status-active {
          background-color: var(--Green-10);
          border-color: var(--Green-20);
        }

        // Disable status (warning type)
        &.status-disable {
          background-color: var(--Yellow-10);
          border-color: var(--Yellow-20);
        }

        // Draft status (default type)
        &.status-draft {
          background-color: var(--Black-20);
          border-color: var(--Black-20);
        }
      }

      .status-date {
        color: var(--Text-secondary);
        line-height: 1.3;
        margin-top: 4px;
      }
    }

    // Actions column styles
    .actions-cell {

      .el-button {
        padding: 4px;
        color: var(--Text-secondary);
        display: block;
        margin: 0;


        &.delete-btn {
          color: var(--Red-30);
        }

        svg {
          width: 14px;
          height: 14px;
        }
      }
    }
  }
}
</style>